'use client';
import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { DialogDescription, DialogTrigger } from '@radix-ui/react-dialog';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useCheckout } from '@/context/checkoutProvider';
import { useAccount, useCart } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { PathNames, getPageUrl, getAgentName } from '@/helpers/utils/siteUtils';
import { CANCEL_TYPE } from '../../../../../constants/constant';
import { CANCEL_ORDER_EVENT_TYPES, CANCEL_ORDER_MESSAGE } from '../../cart/constants';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import { Button } from '../../ui/button';
import { Label } from '../../ui/customLabel/customLabel';
import { RadioGroup, RadioGroupItem } from '../../ui/customRadio/customRadio';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';

const CancelOrderModal = ({
  showOptions,
  cancelType,
  Texts,
  CancelEvent,
  OrderLines,
  className,
  comercetoolsOrder,
  getUpdatedData,
}: any) => {
  const { toast } = useToast();
  const { cancelOrder } = useCart(false);
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const { resetContextValues } = useCheckout();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const params = useSearchParams();
  const { getOrderEvents } = useAccount();
  const [agentName] = useState(getAgentName().replace(/@.*/, ''));

  const CancelOrder = async () => {
    setLoading(true);
    const payload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
      },
      data: { eventType: CancelEvent, createdBy: agentName },
    } as any;
    if (CancelEvent === CANCEL_ORDER_EVENT_TYPES.ENTIRE_ORDER && comercetoolsOrder) {
      resetContextValues();
      await cancelOrder();
      setOpen(false);
      const customerDetailPage = await getPageUrl(PathNames.CUSTOMER_DETAILS);
      router.push(customerDetailPage);
      return;
    } else if (CancelEvent === CANCEL_ORDER_EVENT_TYPES.LINE_ITEM && !comercetoolsOrder) {
      payload.data['OrderLines'] = [{ lineId: OrderLines, message: selectedValue }];
    } else if (CancelEvent === CANCEL_ORDER_EVENT_TYPES.ENTIRE_ORDER) {
      payload.data['message'] = selectedValue;
    }
    if (!selectedValue) {
      setLoading(false);
      return setError(Texts.selectReason);
    }
    await getOrderEvents(payload)
      .then((response) => {
        if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
          if (getUpdatedData) {
            getUpdatedData?.();
          }
          SuccessNotification(toast, {
            description: cancelType !== CANCEL_TYPE.ITEM ? Texts?.orderCancelSuccess : Texts?.orderItemCancelSuccess,
            action: <SuccessIcon className="h-5 w-5" />,
          });
        } else {
          ErrorNotification(toast, {
            description: cancelType !== CANCEL_TYPE.ITEM ? Texts?.orderCancelError : Texts.orderItemCancelFailure,
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }
      })
      .catch((error) => console.error(error));
    setLoading(false);
    setOpen(false);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          setSelectedValue('');
          setError('');
        }
      }}
    >
      <DialogTrigger className={className}>
        {cancelType === CANCEL_TYPE.ITEM ? Texts.cancelItem : Texts.cancelOrder}
      </DialogTrigger>
      <DialogContent className="max-w-[520px] bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">
              {cancelType === CANCEL_TYPE.ITEM ? Texts.cancelItem : Texts.cancelOrder}
            </div>
          </DialogTitle>
          <DialogDescription>
            {showOptions ? (
              <section>
                <p className="mb-2 text-base font-normal">
                  {Texts.cancelOrderHeading}{' '}
                  <span className="font-bold text-scarletRed">
                    {cancelType === CANCEL_TYPE.ITEM ? Texts.cancelItemSubHeading : Texts.cancelOrderSubHeading}
                  </span>
                  ?{' '}
                </p>
                <p className="text-base font-normal">{Texts.pleaseSelectReason}:</p>
              </section>
            ) : (
              <section>
                <p className="mb-2 text-base font-normal">
                  <span>{Texts.cancelOrderHeading} </span>
                  <span className="inline-block font-bold text-scarletRed">
                    {cancelType === CANCEL_TYPE.ITEM ? Texts.cancelItemSubHeading : Texts.cancelOrderSubHeading}?
                  </span>{' '}
                  <span className={`${cancelType === CANCEL_TYPE.ITEM ? 'hidden' : ''}`}>{Texts.allInfoDeleted}</span>
                </p>
              </section>
            )}
          </DialogDescription>
        </DialogHeader>
        {showOptions ? (
          <section className="mt-4">
            <RadioGroup onValueChange={setSelectedValue}>
              <span className="mb-4 mr-4 flex items-center">
                <RadioGroupItem
                  value={CANCEL_ORDER_MESSAGE.OPREATOR_CHANGE}
                  id={CANCEL_ORDER_MESSAGE.OPREATOR_CHANGE}
                  className="mr-2 border-black"
                />
                <Label htmlFor={CANCEL_ORDER_MESSAGE.OPREATOR_CHANGE}>{Texts.operatorChange}</Label>
              </span>
              <span className="mb-4 mr-4 flex items-center">
                <RadioGroupItem
                  value={CANCEL_ORDER_MESSAGE.BACKORDER}
                  id={CANCEL_ORDER_MESSAGE.BACKORDER}
                  className="mr-2 border-black"
                />
                <Label htmlFor={CANCEL_ORDER_MESSAGE.BACKORDER}>{Texts.customerReqBackOrder}</Label>
              </span>
              <span className="mb-4 mr-4 flex items-center">
                <RadioGroupItem
                  value={CANCEL_ORDER_MESSAGE.CHANGE_MIND}
                  id={CANCEL_ORDER_MESSAGE.CHANGE_MIND}
                  className="mr-2 border-black"
                />
                <Label htmlFor={CANCEL_ORDER_MESSAGE.CHANGE_MIND}>{Texts.customerReqChangeMind}</Label>
              </span>
            </RadioGroup>
          </section>
        ) : (
          <></>
        )}
        {error && <div className="font-semibold text-scarletRed">{error}</div>}
        <section className="mt-4 flex justify-start gap-4">
          <Button
            className="rounded-md bg-deepBlue p-4 text-white"
            onClick={() => {
              CancelOrder();
            }}
          >
            {cancelType !== CANCEL_TYPE.ITEM ? Texts.cancelOrder : Texts.cancelItem}
          </Button>
          <Button className="p-0 underline" onClick={() => setOpen(false)}>
            {Texts.returnOrder}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default CancelOrderModal;
